import { StyledHeaderMain, StyledMaxWidthRow } from "@/styles/MainLayouts";
import { Button, Col, Dropdown, Image, MenuProps } from "antd";
import { useRouter } from "next/router";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { HomeDiv, HeaderWrapper } from "./Style";
import {
  IconArrowLeft,
  IconBilling,
  IconProjectDownload,
  IconScormPublished,
  IconScormRepublish,
  IconUser,
  IconWhiteLogout,
  IconWorkspaceHyperlink,
  UpgradeIcon,
} from "@/components/common/Icons";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import { signOut, useSession } from "next-auth/react";
import {
  CustomItem,
  LogoutItem,
  ProfileIcon,
  UserInfoDiv,
} from "@/components/layout/MainHeader";
import { useGetEnvQuery } from "@/redux/api/commonApi";
import { useTranslation } from "next-i18next";
import axios from "axios";
import { UserOutlined } from "@ant-design/icons";
import { projectActions } from "@/redux/stores/projectStore";
import { MODAL_NAME } from "@/components/editor/stripe/constants";
import {
  MyProjectItemDetail,
  UserProjectShareType,
} from "@/types/dashboard.api";
import { getDashBoardState } from "@/redux/store";
import { isTranslateVideoProject } from "@/lib/tools/utils/translatevideos";
import { useCheckDevice } from "@/hooks/useCheckDevice";
import MobileSubscriptionModal from "@/components/common/modal/MobileSubscriptionModal";
import { useGetSubscriptionsQuery } from "@/redux/api/userApi";
import styled from "styled-components";
import classNames from "classnames";
import { poppins } from "@/components/common/fonts";
import { PublishVideoDropdown } from "./PublishVideoDropdown";
import { useNotification } from "@/hooks/useNotification";
import { ScormVersion } from "@/constants/scorm";
import { useSubscriptionModal } from "@/hooks/useSubscriptionModal";
import { ProjectForPublishScorm, PublishedScorm } from "@/types/scorm";
import { scormApi } from "@/redux/api/scorm/scormApi";
import { downloadSRT } from "@/lib/subtitle-utils";
import { UnpublishConfirmModal } from "@/components/scorm/modal/UnpublishConfirmModal";

import { VersionSelect } from "./VersionSelect";
import { ShareVideoDropdown } from "./ShareVideoDropdown";
import { DownloadDropdown } from "./DownloadDropdown";
import { useSubscription } from "@/hooks/useSubscription";
import { usePathname } from "next/navigation";

type ShareHeaderProps = {
  projectShareMember?: UserProjectShareType[] | [] | undefined;
  setProjectShareMember?: Dispatch<SetStateAction<UserProjectShareType[]>>;
  projectInfo?: MyProjectItemDetail;
  userProjectPermission?: string;
  publishedScorm?: PublishedScorm;
  versions?: ProjectForPublishScorm[];
};

export default function ShareHeader(props: ShareHeaderProps) {
  const {
    projectShareMember,
    projectInfo,
    userProjectPermission,
    publishedScorm,
    versions = [],
  } = props;
  const { contextHolder, notify } = useNotification();
  const dispatch = useAppDispatch();
  const [isEditDisabled, setIsEditDisabled] = useState<boolean>(false);
  const { permission } = useSubscription();
  const { accountUserInfo, actionLoading } = useAppSelector((store) =>
    getDashBoardState(store),
  );

  // NOTE: 자동화 비디오 더빙 프로젝트의 경우, 팀플랜 이상이면 SRT 추출 가능
  const prebuild = projectInfo?.prebuild ?? "";
  const lowerCasedUserPlan = accountUserInfo?.plan?.toLowerCase();
  const isTeamOrEnterprisePlan = useMemo(() => (
    lowerCasedUserPlan?.includes("team") ||
    lowerCasedUserPlan?.includes("enterprise") ||
    accountUserInfo?.enterprise
  ), [accountUserInfo?.enterprise, lowerCasedUserPlan]);
  const isTranslateVideo = isTranslateVideoProject(prebuild);

  const canExport4k = useMemo(() => {
    if (!permission?.export4k) {
      return { is: false, why: "plan" };
    }

    if (projectInfo?.downloadUrlResolution === "4k" || isTranslateVideo) {
      return { is: false, why: "resolution" };
    }

    return { is: true, why: "" };
  }, [isTranslateVideo, permission?.export4k, projectInfo?.downloadUrlResolution]);

  const { open: openSubscriptionModal} = useSubscriptionModal();

  const { data: userSubscription } = useGetSubscriptionsQuery(null);

  const router = useRouter();

  const [isRouting, setIsRouting] = useState(false);

  useEffect(() => {
    const handleRouteChangeStart = () => setIsRouting(true);
    const handleRouteChangeComplete = () => setIsRouting(false);

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeComplete);
    };
  }, [router.events]);

  const session = useSession();
  const backgroundColor = useMemo(
    () => "#" + Math.floor(Math.random() * 16777215).toString(16),
    [],
  );
  const { t } = useTranslation("common");
  const { isMobile, isHybridApp } = useCheckDevice();
  const [modalOpen, setModalOpen] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(false);

  const accountUrl = useGetEnvQuery(
    { key: "ACCOUNT_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    },
  );

  useEffect(() => {
    if (userSubscription?.plan === "none") setIsFreeUser(true);
  }, [userSubscription?.plan]);

  useEffect(() => {
    if (session?.data?.user?.id) {
      if (projectInfo?.userId === session?.data?.user?.id) {
        setIsEditDisabled(false);
      } else {
        const findMember = projectShareMember?.find(
          (member) =>
            member.id == session?.data?.user?.id &&
            member?.sharePermission &&
            member?.sharePermission == "edit",
        );
        if (findMember) {
          setIsEditDisabled(false);
        } else {
          if (userProjectPermission && userProjectPermission === "edit") {
            setIsEditDisabled(false);
          } else {
            setIsEditDisabled(true);
          }
        }
      }
    } else {
      setIsEditDisabled(true);
    }
    if (isTranslateVideo) {
      setIsEditDisabled(true);
    }
  }, [
    projectInfo?.userId,
    projectShareMember,
    session?.data?.user?.id,
    userProjectPermission,
    isTranslateVideo,
  ]);

  const goToDashboard = useCallback(() => {
    if (router) {
      if (isHybridApp) {
        router.back();
      } else {
        router.push("/dashboard");
      }
    }
  }, [router, isHybridApp]);

  const handleExport4k = useCallback(() => {
    if (!canExport4k.is) {
      if (canExport4k.why === "plan") {
        openSubscriptionModal({
          type: "locked",
          message: [t("By upgrading your plan, you'll unlock access to even more powerful features.")],
        });
      }
      return;
    }

    if (!projectInfo || !projectInfo._id || !projectInfo.download_url) {
      alert("An error occurred during export. Please try again later.");
      return;
    }

    dispatch(dashboardActions.setActionLoading(true));

    axios.post("/api/project/upscale", {
      projectId: projectInfo._id,
      videoUrl: projectInfo.download_url,
    })
      .then((r) => {
        if (!r.data?.success) {
          throw new Error();
        }

        router.push("/dashboard").then(() => {
          dispatch(dashboardActions.setActionLoading(false));
        });
      })
      .catch(() => {
        dispatch(dashboardActions.setActionLoading(false));
        alert(t("An error occurred during export. Please try again later."));
      })
  }, [canExport4k.is, canExport4k.why, dispatch, openSubscriptionModal, projectInfo, router, t]);

  const handleDownload = useCallback(() => {
    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "video_download",
        project_id: projectInfo?.name,
      });
    }

    if (projectInfo?.download_url) {
      const videoTitle = (() => {
        const name = projectInfo?.name ?? "video";

        if (projectInfo?.downloadUrlResolution === "4k") {
          return `[4k] ${name}`;
        }

        return name;
      })();
      dispatch(dashboardActions.setActionLoading(true));
      dispatch(
        dashboardActions.downloadVideo({
          videoUrl: projectInfo.download_url,
          videoTitle,
        }),
      ).finally(() => {
        dispatch(dashboardActions.setActionLoading(false));
      });
    }
  }, [dispatch, projectInfo]);

  const handelEdit = useCallback(() => {
    if (!isEditDisabled && router) {
      router.push(`/editor/${projectInfo?._id}`);
    }
  }, [isEditDisabled, projectInfo?._id, router]);

  // NOTE: 자동화 더빙 Job에서 srt text를 api로 받아와 파일로 다운로드함
  const handleExtractSrt = useCallback(async () => {
    if (actionLoading) {
      return;
    }

    dispatch(dashboardActions.setActionLoading(true));

    try {
      if (!permission?.export4k) {
        openSubscriptionModal({
          type: "locked",
          message: [t("By upgrading your plan, you'll unlock access to even more powerful features.")],
        });

        return;
      }

      const { data: srtContent } = await axios.get(
        `/api/automation/translatevideo/get-srt?projectId=${projectInfo?._id}`,
      );
      downloadSRT(srtContent, projectInfo?.name ?? "subtitle");
    } catch (e) {
      notify.warning(
        t("An error occurred with the service. Please try again later."),
      );
    } finally {
      dispatch(dashboardActions.setActionLoading(false));
    }
  }, [
    actionLoading,
    dispatch,
    notify,
    openSubscriptionModal,
    permission?.export4k,
    projectInfo?._id,
    projectInfo?.name,
    t,
  ]);

  const getSession = useCallback(async () => {
    const res = await axios.post("/api/auth/preTransfer");
    const { data } = res;
    if (data.success) {
      console.log(data.data.sessionId);
      return data.data.sessionId;
    }
    return "";
  }, []);

  const onClickProfile = async () => {
    const sessionId = await getSession();
    if (sessionId.length > 0) {
      console.log(accountUrl.data);
      router?.replace(
        `${accountUrl.data}/sso?sessionId=${sessionId}&path=profile&service=aistudios`,
      );
    }
  };

  const onClickBilling = async () => {
    const sessionId = await getSession();
    if (sessionId.length > 0) {
      router?.replace(
        `${accountUrl.data}/sso?sessionId=${sessionId}&path=billing&service=aistudios`,
      );
    }
  };

  const handleSignInOut = () => {
    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "log_out",
      });
    }

    signOut();
  };

  /**
   * G2 리뷰 관련 처리들...
   */
  const { accountUserInfo: userInfo } = useAppSelector(
      (store) => getDashBoardState(store)
    );

  const isLoggedIn = !!userInfo?.id
  
  const handleClickOpenG2Review = useCallback(() => {
    if (!isLoggedIn) {
      return
    }

    dispatch(dashboardActions.setG2ReviewModalOpen({ isShow: true }));
  }, [isLoggedIn, dispatch])

  const items: MenuProps["items"] = [
    {
      label: (
        <UserInfoDiv>
          <ProfileIcon>
            {session?.data?.user?.email?.charAt(0).toUpperCase() || ""}
          </ProfileIcon>
          <div style={{ marginLeft: "10px" }}>
            <div>{session?.data?.user?.name}</div>
            <div>{session?.data?.user?.email}</div>
          </div>
        </UserInfoDiv>
      ),
      key: "accountInfo",
    },
    ...(!isMobile
      ? [
          {
            label: (
              <CustomItem onClick={onClickProfile}>
                <IconUser style={{ verticalAlign: "middle" }} />
                {t("Profile")}
              </CustomItem>
            ),
            key: "manageAccount",
          },
          {
            label: (
              <CustomItem onClick={onClickBilling}>
                <IconBilling style={{ verticalAlign: "middle" }} />
                {t("Billing")}
              </CustomItem>
            ),
            key: "billing",
          },
        ]
      : []),
    ...(isMobile
      ? [
          {
            label: (
              <LogoutItem onClick={handleClickOpenG2Review}>
                🌟 {t("Write a G2 Review")}
              </LogoutItem>
            ),
            key: "g2Review",
          },
        ]
      : []),
    {
      label: (
        <LogoutItem onClick={handleSignInOut}>
          <IconWhiteLogout style={{ verticalAlign: "middle" }} />
          {t("Log Out")}
        </LogoutItem>
      ),
      key: "logout",
    },
  ];

  const handleOpenLoginModal = () => {
    dispatch(projectActions.setOpenedModal(MODAL_NAME.LOGIN));
  };

  /**
   * 현재 프로젝트가 내 프로젝트 또는 워크스페이스 공유 프로젝트인지 여부
   */
  const isMyOrWorkspaceSharedProject = useMemo(() => {
    return (
      projectInfo?.userId === session?.data?.user?.id ||
      projectInfo?.workspaceId?.includes(accountUserInfo?.workspaceId ?? "")
    );
  }, [
    accountUserInfo.workspaceId,
    projectInfo?.userId,
    projectInfo?.workspaceId,
    session?.data,
  ]);

  /**
   * SCORM 패키징 관련
   */
  const canExportScormPackage =
    isMyOrWorkspaceSharedProject && accountUserInfo.enterprise;

  const [publishMutation, { isLoading: isPublishing }] =
    scormApi.usePublishMutation();
  const [unpublishMutation, { isLoading: isUnpublishing }] =
    scormApi.useUnpublishMutation();
  const [republishMutation, { isLoading: isRepublishing }] =
    scormApi.useRepublishMutation();

  const isMutatingScorm = isPublishing || isUnpublishing || isRepublishing;

  const [isOpenedUnpublishConfirmModal, setIsOpenedUnpublishConfirmModal] =
    useState(false);

  const handleOpenUnpublishConfirmModal = useCallback(() => {
    setIsOpenedUnpublishConfirmModal(true);
  }, []);

  const handleCancelUnpublishConfirmModal = useCallback(() => {
    if (isMutatingScorm) {
      return;
    }

    setIsOpenedUnpublishConfirmModal(false);
  }, [isMutatingScorm]);

  const handlePublishAndDownload = useCallback(
    async ({
      scormVersion,
      completionCriteria,
    }: {
      scormVersion: ScormVersion;
      completionCriteria: number;
    }) => {
      if (!isMyOrWorkspaceSharedProject) {
        return;
      }

      if (!accountUserInfo.enterprise) {
        openSubscriptionModal({
          type: "locked",
          message: [t("By upgrading your plan, you'll unlock the ability to export SCORM packages.")],
        });

        return;
      }

      if (!projectInfo?._id) {
        return;
      }

      const baseInfoId = projectInfo.baseInfoId;
      const projectId = projectInfo._id;

      if (!baseInfoId) {
        return;
      }

      try {
        await publishMutation({
          baseInfoId,
          projectId,
          scormVersion,
        }).unwrap();

        const a = document.createElement("a");
        a.href = `/api/scorm/${baseInfoId}/package?completionCriteria=${completionCriteria}`;
        a.click();
        a.remove();

        notify.success(
          t("SCORM package download has been completed successfully."),
        );
      } catch (e) {
        notify.error(
          t("An error while publishing your video. Please try again later."),
        );
      }
    },
    [
      isMyOrWorkspaceSharedProject,
      accountUserInfo,
      openSubscriptionModal,
      projectInfo,
      publishMutation,
      t,
      notify,
    ],
  );

  const handleUnpublish = useCallback(async () => {
    if (!publishedScorm) {
      return;
    }

    try {
      await unpublishMutation({
        baseInfoId: publishedScorm.baseInfoId,
      }).unwrap();

      notify.success(t("The video has been unpublished."));

      setIsOpenedUnpublishConfirmModal(false);
    } catch (e) {
      notify.error(
        t("An error while unpublishing your video. Please try again later."),
      );
    }
  }, [publishedScorm, unpublishMutation, notify, t]);

  const handleRepublish = useCallback(async () => {
    if (!publishedScorm) {
      return;
    }

    if (!projectInfo?._id) {
      return;
    }

    const currentVersion = publishedScorm.project.version;
    const replaceVersion = versions.find(
      (version) => version._id === projectInfo?._id,
    )?.version;

    try {
      await republishMutation({
        baseInfoId: publishedScorm.baseInfoId,
        projectId: projectInfo._id,
      }).unwrap();

      notify.success(
        t(
          `Version {{currentVersion}} video has been republished as Version {{replaceVersion}}`,
          {
            currentVersion,
            replaceVersion,
          },
        ),
      );
    } catch (e) {
      notify.error(
        t("An error while republishing your video. Please try again later."),
      );
    }
  }, [publishedScorm, projectInfo, republishMutation, notify, t, versions]);

  const isShowArrow = (usePathname() !== "/dashboard") && isHybridApp;

  return (
    <StyledHeaderMain>
      <ShareHeaderWrapper
        className={classNames({
          mobile: isMobile,
        })}
      >
        <Col>
          <HomeDiv onClick={goToDashboard} isShowArrow={isShowArrow ?? false}>
            {
              isShowArrow ? (
                <IconArrowLeft />
              ) : (
                <Image
                  src="/images/logo_studios_renewal.svg"
                  preview={false}
                  alt="logo"
                />
              )
            }
          </HomeDiv>
        </Col>
        {!isMobile && (
          <TitleAndEditArea>
            <Title title={projectInfo?.name}>{projectInfo?.name}</Title>

            {/* 
              버전 표시/선택 컨트롤은 권한이 있을 때만 표시됨
            */}
            {userProjectPermission === "edit" && (
              <VersionSelect
                projectId={projectInfo?._id}
                versions={versions}
                onChange={(projectId) => {
                  if (router) {
                    router.push(`/share/${projectId}`);
                  }
                }}
                loading={isRouting}
                isHiddenPublished={!canExportScormPackage}
              />
            )}

            {userProjectPermission === "edit" && (
              <EditButton
                onClick={handelEdit}
                disabled={isEditDisabled}
                loading={isRouting}
              >
                {t("Edit")}
              </EditButton>
            )}
          </TitleAndEditArea>
        )}
        <Col>
          <HeaderWrapper>
            {session.status === "unauthenticated" ? (
              /**
               * 로그인이 되어 있지 않은 경우
               */
              <>
                <div className="button-wrapper">
                  <ShareVideoDropdown project={projectInfo}>
                    <ShareButton
                      icon={<IconWorkspaceHyperlink />}
                      onClick={() => {
                        if (window?.dataLayer?.push) {
                          window.dataLayer.push({
                            event: "video_share",
                            project_id: projectInfo?.name,
                          });
                        }
                      }}
                    >
                      {t("Share")}
                    </ShareButton>
                  </ShareVideoDropdown>

                  <Button
                    className="free-button"
                    onClick={() => {
                      if (router) {
                        router.push("/dashboard");
                      }
                    }}
                  >
                    {t("Get Started for Free")}
                  </Button>
                  <div className="profile-button">
                    <ProfileIcon
                      className="unauthorized"
                      onClick={handleOpenLoginModal}
                    >
                      <UserOutlined />
                    </ProfileIcon>
                  </div>
                </div>
              </>
            ) : (
              /**
               * 로그인이 되어 있는 경우
               */
              <>
                <div className="button-wrapper">
                  <DownloadDropdown
                    onExport4k={handleExport4k}
                    onDownloadVideo={handleDownload}
                    onDownloadSrt={handleExtractSrt}

                    canExport4k={canExport4k}
                    canDownloadSrt={isTeamOrEnterprisePlan}
                    isOwner={isMyOrWorkspaceSharedProject}
                    hasSrt={isTranslateVideoProject(prebuild)}

                    isLoadingExport4k={actionLoading}
                    isLoadingDownloadVideo={actionLoading}
                    isLoadingDownloadSrt={actionLoading}
                  >
                    <DownloadButton
                      loading={actionLoading}
                      disabled={actionLoading}
                      icon={<IconProjectDownload />}
                    >
                      {t("Download")}
                    </DownloadButton>
                  </DownloadDropdown>

                  {isMyOrWorkspaceSharedProject ? (
                    /**
                     * 내 프로젝트 또는 워크스페이스 공유 프로젝트인 경우
                     */
                    <PublishVideoDropdown
                      project={projectInfo}
                      showCopyCdnLink={
                        isMyOrWorkspaceSharedProject &&
                        accountUserInfo?.company?.cdn
                      }
                      canExportScormPackage={canExportScormPackage}
                      publishedScorm={publishedScorm}
                      versions={versions}
                      isMutatingScorm={isMutatingScorm}
                      onPublishAndDownload={handlePublishAndDownload}
                      onUnpublish={handleOpenUnpublishConfirmModal}
                      onRepublish={handleRepublish}
                    >
                      <PublishButton
                        type="primary"
                        className={classNames({
                          published: canExportScormPackage && !!publishedScorm,
                        })}
                        icon={
                          !canExportScormPackage || !publishedScorm ? (
                            <IconWorkspaceHyperlink />
                          ) : publishedScorm.projectId === projectInfo?._id ? (
                            <IconScormPublished />
                          ) : (
                            <IconScormRepublish />
                          )
                        }
                        onClick={() => {
                          if (window?.dataLayer?.push) {
                            window.dataLayer.push({
                              event: "video_share",
                              project_id: projectInfo?.name,
                            });
                          }
                        }}
                      >
                        {!canExportScormPackage || !publishedScorm
                          ? t("Publish")
                          : publishedScorm.projectId === projectInfo?._id
                          ? t("Published")
                          : t("Republish")}
                      </PublishButton>
                    </PublishVideoDropdown>
                  ) : (
                    /**
                     * 내 프로젝트 또는 워크스페이스 공유 프로젝트가 아닌 경우
                     */
                    <ShareVideoDropdown project={projectInfo}>
                      <ShareButton
                        icon={<IconWorkspaceHyperlink />}
                        onClick={() => {
                          if (window?.dataLayer?.push) {
                            window.dataLayer.push({
                              event: "video_share",
                              project_id: projectInfo?.name,
                            });
                          }
                        }}
                      >
                        {t("Share")}
                      </ShareButton>
                    </ShareVideoDropdown>
                  )}
                </div>

                {isMobile && isFreeUser && !isHybridApp && (
                  <Button
                    className="upgrade-button"
                    onClick={() => setModalOpen(true)}
                    icon={<UpgradeIcon width={16} height={14} fill="white" />}
                    style={{
                      backgroundColor: "#3075f6",
                      border: "1px solid #3075f6",
                      color: "#ffffff",
                    }}
                  >
                    {t("Upgrade")}
                  </Button>
                )}

                <div className="profile-button">
                  {isHybridApp ? (
                    <ProfileIcon style={{ backgroundColor }} onClick={() => router.push("/hybrid-app/setting")}>
                      {session?.data?.user?.id?.charAt(0)?.toLocaleUpperCase() || ""}
                    </ProfileIcon>
                  ) : (
                    <Dropdown menu={{ items }} trigger={["click"]}>
                      <ProfileIcon style={{ backgroundColor }}>
                        {session?.data?.user?.id?.charAt(0)?.toLocaleUpperCase() || ""}
                      </ProfileIcon>
                    </Dropdown>
                  )}
                </div>
              </>
            )}
          </HeaderWrapper>
        </Col>
      </ShareHeaderWrapper>
      <MobileSubscriptionModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
      />

      <UnpublishConfirmModal
        isOpened={isOpenedUnpublishConfirmModal}
        isMutatingScorm={isMutatingScorm}
        onConfirm={handleUnpublish}
        onCancel={handleCancelUnpublishConfirmModal}
      />

      {contextHolder}
    </StyledHeaderMain>
  );
}

/**
 * - &.mobile
 */
const ShareHeaderWrapper = styled(StyledMaxWidthRow)`
  &:not(.mobile) {
    display: grid;
    grid-template-columns: minmax(auto, 1fr) minmax(0, auto) 1fr;
    gap: 10px;
  }
`;

const TitleAndEditArea = styled(Col)`
  display: flex;

  gap: 6px;
  align-items: center;
`;

const Title = styled.div`
  color: #000;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const EditButton = styled(Button)`
  flex-shrink: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 80px;
  height: 40px;

  border-radius: 7px;
  border: 1px solid #9e9e9e;
  background: #fff;

  color: #9e9e9e;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;
`;

const DownloadButton = styled(Button)`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  width: 120px;
  height: 40px;

  border-radius: 7px;
  border: 1px solid #9e9e9e;
  background: #fff;

  color: #9e9e9e;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;

  svg {
    stroke: currentColor;
  }
`;

/**
 * - &.published
 */
const PublishButton = styled(Button)`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  width: 120px;
  height: 40px;

  border-radius: 7px;

  color: #fff;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;

  svg,
  svg g,
  svg path {
    stroke: currentColor;
  }

  &.published {
    background-color: #e1eaff;
    color: #4276ee;
  }
`;

const ShareButton = styled(Button)`
  flex-shrink: 0;

  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  width: 120px;
  height: 40px;

  border-radius: 7px;
  border: 1px solid #9e9e9e;
  background: #fff;

  color: #9e9e9e;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;

  svg {
    stroke: currentColor;
  }
`;
